import { Button, Select } from "antd";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import CustomModal from "../../../components/ui/CustomModal";
import { useImportGlobalHolidayMutation } from "../../../features/holiday/holidayApi";
import { toast } from "../../../lib/toast";

type Props = {
  open: boolean;
  handleClose: () => void;
};

export default function HolidayImportModal({ open, handleClose }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | undefined>(
    new Date().getFullYear()
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importGlobalHoliday, { isLoading }] = useImportGlobalHolidayMutation();

  useEffect(() => {
    if (!open) {
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSelectedYear(new Date().getFullYear());
    }
  }, [open]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const isExcel =
      selectedFile?.name?.toLowerCase().endsWith(".xlsx") ||
      selectedFile?.name?.toLowerCase().endsWith(".xls");

    if (!isExcel) {
      toast("error", "Please upload only excel file");
      event.target.value = "";
      return;
    }

    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      toast("error", "Please choose an excel file first");
      return;
    }
    if (!selectedYear) {
      toast("error", "Please select year");
      return;
    }

    try {
      const response: any = await importGlobalHoliday({
        file,
        year: selectedYear,
      }).unwrap();
      toast(
        "success",
        response?.message || "Holiday calendar imported successfully"
      );
      handleClose();
    } catch (error: any) {
      const serverMessage = error?.data?.message;
      const normalizedServerMessage =
        Array.isArray(serverMessage) && serverMessage.length
          ? serverMessage.join(", ")
          : typeof serverMessage === "string"
          ? serverMessage
          : undefined;
      const message =
        normalizedServerMessage ||
        error?.error ||
        error?.message ||
        "Holiday import failed";
      toast("error", message);
    }
  };

  return (
    <CustomModal
      width={560}
      title="Import Global Holiday Calendar"
      open={open}
      handleClose={handleClose}
      footer={null}
    >
      <div className="p-6">
        <div className="mb-4">
          <p className="text-sm text-[#3d4a5c] mb-2">Select Year</p>
          <Select
            value={selectedYear}
            onChange={(value) => setSelectedYear(value)}
            options={Array.from({ length: 7 }, (_, index) => {
              const year = 2024 + index;
              return { value: year, label: String(year) };
            })}
            className="w-full"
            disabled={isLoading}
          />
        </div>

        <p className="text-sm text-[#6B7280] mb-3">
          Upload `.xlsx` or `.xls` file. Required columns:
          <b> Holiday + Date</b> or <b>Holiday + Start Date + End Date</b>.
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          className="mb-4 block w-full text-sm"
        />

        <p className="text-sm text-[#3d4a5c] mb-6">
          {file?.name || "No file chosen"}
        </p>

        <div className="flex items-center gap-1.5 mt-1 mb-6">
          <span className="text-body-text text-sm leading-normal">
            Download Sample File
          </span>
          <Button
            className="download-button !p-0 !h-fit !border-none !shadow-none !font-semibold"
            size="small"
            icon={<DownloadIcon />}
            type="link"
            href="/Global Holiday Import Template.xlsx"
            target="_blank"
            download
          >
            Download
          </Button>
        </div>

        <div className="flex justify-end gap-2">
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleUpload} loading={isLoading}>
            Upload
          </Button>
        </div>
      </div>
    </CustomModal>
  );
}
